import { useEffect, useState, type FormEvent } from 'react'
import { api, ApiError } from '../api'
import type { CurrentUser } from '../types'
import { Field, Notice } from '../components/ui'

/**
 * Демо-учётки показываются только там, где демо-данные действительно
 * залиты: сервер отвечает на это отдельным флагом.
 *
 * На системе заказчика этот блок не просто бесполезен — он предлагает
 * войти паролями, которых нет, и выглядит как поломка входа.
 */
const DEMO_USERS = [
  { email: 'ceo@svodka.ru', password: 'ceo-demo-2026', role: 'Генеральный директор — всё' },
  { email: 'cfo@svodka.ru', password: 'cfo-demo-2026', role: 'Финдир — финансы и продажи' },
  { email: 'ekb@svodka.ru', password: 'ekb-demo-2026', role: 'Директор филиала — только Екатеринбург' },
  { email: 'area@svodka.ru', password: 'area-demo-2026', role: 'Управляющий — две точки, полгода' },
  { email: 'logist@svodka.ru', password: 'logist-demo-2026', role: 'Логистика — только склад' },
  { email: 'admin@svodka.ru', password: 'admin-demo-2026', role: 'Администратор — права и журнал' },
]

export function Login({
  onLogin,
  notice = null,
}: {
  onLogin: (user: CurrentUser) => void
  /** Почему человек снова на входе — например, сессия закончилась. */
  notice?: string | null
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [demo, setDemo] = useState(false)

  useEffect(() => {
    // Молча ничего не показываем, если запрос не удался: лучше не показать
    // подсказку в демо, чем показать её в бою.
    fetch('/api/auth/context', { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : { demo: false }))
      .then((context: { demo?: boolean }) => setDemo(Boolean(context.demo)))
      .catch(() => setDemo(false))
  }, [])

  const submit = async (event: FormEvent, credentials?: { email: string; password: string }) => {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      const result = await api.login(credentials?.email ?? email, credentials?.password ?? password)
      onLogin(result.user)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось войти')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <div className="sidebar__mark" aria-hidden="true">
            С
          </div>
          <div>
            <div className="sidebar__title" style={{ fontSize: 'var(--text-lg)' }}>
              Сводка
            </div>
            <div className="tiny muted">Управленческая отчётность</div>
          </div>
        </div>

        <form onSubmit={(event) => submit(event)} className="stack" style={{ gap: 14 }}>
          <Field label="Почта">
            <input
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.ru"
              required
            />
          </Field>

          <Field label="Пароль">
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </Field>

          {notice && !error && <div className="login__notice"><Notice tone="info">{notice}</Notice></div>}
          {error && <Notice tone="bad">{error}</Notice>}

          <button className="btn btn--primary" type="submit" disabled={busy} style={{ width: '100%', padding: 9 }}>
            {busy ? 'Проверяем…' : 'Войти'}
          </button>
        </form>

        {demo && (
        <div className="login__demo fin-appear">
          <div className="login__demo-title">Демонстрационные учётные записи</div>
          {DEMO_USERS.map((user) => (
            <button
              key={user.email}
              className="demo-user"
              type="button"
              onClick={(event) => submit(event, { email: user.email, password: user.password })}
            >
              <span className="demo-user__name">{user.email}</span>
              <span className="demo-user__role truncate">{user.role}</span>
            </button>
          ))}
          <div className="tiny muted" style={{ marginTop: 8 }}>
            Пароли известные — только для демонстрации. Права у всех разные: сравните, что видит директор
            филиала и что видит логист.
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
